import React, { useEffect, useMemo, useState } from 'react'
import {  Alert, FormPro } from '@roo/roo'
import  Radio from '@roo/roo/Radio'
import  Input from '@roo/roo/Input'
import { useTranslation } from '@sailor/i18n-web'
import { FormInstance } from '@roo/roo/FormPro'
import {  BudgetFeeConfigTypeEnum, BudgetFeeTypeFeEnum } from '../utils'
import QuestionTooltip from '@/components/QuestionTooltip'
import useRuleConfig from '@/common/recoil/atom/ruleConfigAtom'
import { getNumStr, isEqualString } from '@/common/utils'
import ExcelInput from './ExcelInput'
import ManageExcelAmount from './ExcelInput/ManageExcelAmount'
import { useFormContext } from '../context/formContext'
import ExpiryConfigFormItem, { IExpiryConfigiFe } from './ExpiryConfigFormItem'
import entFunctionConfigAtom, { ENT_FUCNTION_CODE_ENUM } from '@/common/recoil/atom/entFunctionConfigAtom'
import { getMoneyCurrency } from '@/common/utils/i18nFuncs'
// import { regionFractionPlaces } from '@/common/utils/i18n'


const subFormItemLayout = {
  labelCol: { span: 0 },
  wrapperCol: { span: 9 },
}

type BudgetFormItemsProps = {
  form: FormInstance
  isCreate: boolean
}


/** 循环下发表单项, 包含下发周期,预算额度,有效期 */
const BudgetFormItems = (props: BudgetFormItemsProps) => {
  const { form, isCreate } = props
  const { t } = useTranslation()
  const { budgetFeeConfig } = useRuleConfig() || {}
  const { effectStatusMap } = useFormContext()
  const [hideExcelErr, setHideExcelErr] = useState(false)
  const entFunctionsConfig = entFunctionConfigAtom.useData()
  const canUseExprice = entFunctionsConfig[ENT_FUCNTION_CODE_ENUM.RULE_PERIOD]?.open

  const formValues = {
    budgetAmount: FormPro.useWatch('budgetAmount', form),
    periodType: FormPro.useWatch('periodType', form),
    amountFrom: FormPro.useWatch('amountFrom', form),
    budgetFeeType: FormPro.useWatch('budgetFeeType', form),
  }


  const currentsycleOption = budgetFeeConfig?.periodConfig?.find(item => isEqualString(item.periodType, formValues.periodType))

  const amountWarn = useMemo(() => {
    const limitAmount = Number(currentsycleOption?.amountLimit)
    if (limitAmount && (formValues.budgetAmount > limitAmount)) {
      return `${currentsycleOption?.i18nTextValue}${t('e_pc_budget_amount_too_large', { limit: limitAmount })}`
    }
    return ''
  }, [formValues.budgetAmount, currentsycleOption?.amountLimit, t])

  useEffect(() => {  // 非一次性下发时,选择Excel形式,重置为用户输入形式
    if (formValues.amountFrom === BudgetFeeConfigTypeEnum.EXCEL && formValues.budgetFeeType !== BudgetFeeTypeFeEnum.BUDGET_ONCE) {
      form.setFieldValue('amountFrom', BudgetFeeConfigTypeEnum.INPUT)
    }

  }, [formValues.budgetFeeType, formValues.amountFrom])

  const isLoop =  formValues.budgetFeeType !== BudgetFeeTypeFeEnum.BUDGET_ONCE

  useEffect(() => { // 新建,无下发周期时,默认选择第一个
    if (isCreate && !formValues.periodType && budgetFeeConfig?.periodConfig?.length) {
      const defaultPeriodVal = budgetFeeConfig?.periodConfig[0].periodType
      form.setFieldValue('periodType', getNumStr(defaultPeriodVal))
    }
  }, [isLoop, formValues.periodType, budgetFeeConfig?.periodConfig?.length])

  return (
    <>
      {isLoop && (
        <FormPro.Item  name="periodType" label={t('e_pc_issuance_cycle', '下发周期')} {...subFormItemLayout}
          required
          rules={[{ required: true, message: t('e_pc_select_issuance_cycle', '请选择下发周期') }]}
          onChange={() => {form.resetFields(['budgetAmount'])}}
        >
          <Radio.Group disabled={!isCreate}>
            {budgetFeeConfig?.periodConfig.map(item => (
              <Radio value={getNumStr(item.periodType)} key={item.periodType}>
                {item.i18nTextValue}<QuestionTooltip>{item.tips || ''}</QuestionTooltip>
              </Radio>
            ))}
          </Radio.Group>
        </FormPro.Item>
      )
      }
      {formValues.budgetFeeType === BudgetFeeTypeFeEnum.BUDGET_ONCE && (
        <FormPro.Item label={t('e_pc_quota_type', '额度类型')} {...subFormItemLayout}
          required
          name="amountFrom"
          rules={[{ required: true, message: t('Customer_placeholder_pleaseselect') }]}
          onChange={() => {
            form.resetFields(['budgetAmount', 'initExcelConfig'])
          }}
        >
          <Radio.Group disabled={!isCreate}>
            <Radio value={BudgetFeeConfigTypeEnum.INPUT}>
              {t('e_pc_fixed_amount', '固定金额')}
              <QuestionTooltip>{t('e_pc_onetime_trigger')}</QuestionTooltip>
            </Radio>
            <Radio value={BudgetFeeConfigTypeEnum.EXCEL}>
              {t('e_pc_custom_amount', '自定义')}
              <QuestionTooltip>{t('e_pc_custome_trigger')}</QuestionTooltip>
            </Radio>
          </Radio.Group>
        </FormPro.Item>
      )}
      {formValues.amountFrom === BudgetFeeConfigTypeEnum.EXCEL
        ?  (
          <>
            <FormPro.Item label={t('e_pc_batch_process', '批量处理')} name="initExcelConfig"
              className={hideExcelErr ? 'no_error_text_container' : ''}
              {...subFormItemLayout}
              required rules={[{ required: true, message: t('e_pc_upload_excel_required', '请上传Excel文件') },
              ]}
            >
              <ExcelInput disabled={!effectStatusMap.isNotStart} hideFormTip={setHideExcelErr} />
            </FormPro.Item>
            {
              !effectStatusMap.isNotStart && !(formValues.budgetFeeType === BudgetFeeTypeFeEnum.BUDGET_ONCE && formValues.amountFrom === BudgetFeeConfigTypeEnum.EXCEL) && (
                <FormPro.Item label={t('e_pc_balance_manage', '余额管理')} name="updateExcelConfig"
                  {...subFormItemLayout}
                >
                  <ManageExcelAmount />
                </FormPro.Item>
              )
            }
          </>
        )
        : (
          <FormPro.Item label={t('e_pc_budget_amount', '预算额度')} name="budgetAmount"
            {...subFormItemLayout}
            extra={amountWarn && <Alert title={amountWarn} size="compact" icon="roo-icon-info-circle"/>}
            required
            rules={[
              {
                validator: (_, value) => {
                  const checkValue = value?.trim()
                  const fixedPlace = 2 // NOTE: 应金服要求,固定两位小数,不随region改变.否则支付会有问题
                  if (
                    !checkValue ||
                    Number(checkValue) <= 0 ||
                    !new RegExp(`^(?!0\\d)\\d+(\\.\\d{1,${fixedPlace}})?$`).test(checkValue)
                  ) {
                    return Promise.reject(
                      t(
                        'e_pc_maximum_decimal_places',
                        {
                          pluralCount: fixedPlace,
                          number: fixedPlace,
                        }
                      ) ||  `请输入大于0的整数或小数，最多${fixedPlace}位小数`
                    )
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input
              style={{ width: '80%' }}
              placeholder={t('e_pc_enter_budget_amount', '请输入预算金额')}
              suffix={getMoneyCurrency()}
              disabled={formValues.budgetFeeType === BudgetFeeTypeFeEnum.BUDGET_ONCE && formValues.amountFrom === BudgetFeeConfigTypeEnum.INPUT && !effectStatusMap.isNotStart}
            />
          </FormPro.Item>
        )
      }
      {isLoop && canUseExprice && (
        <FormPro.Item label={t('e_pc_validity_period', '有效期')} name="expiryConfig"
          {...subFormItemLayout}
          required
          tooltip={<span style={{ hyphens: 'auto', wordBreak: 'break-word' }}>{t('e_pc_cross_cycle_usage')}</span>}
          rules={[
            {
              validator: (_, value:IExpiryConfigiFe) => {
                if (value?.needFillValue) {
                  const checkValue = value?.expiryValue?.trim()

                  if (!checkValue || checkValue === '0' || !/^[1-9]\d*$/.test(checkValue)) {
                    return Promise.reject(t('e_c_input_gt_zero', '请输入大于0的整数'))
                  }
                  const valLimit = value.originData?.expiryValueLimit
                  if (valLimit && (Number(checkValue) > valLimit)) { // 超出后端配置的上限
                    return Promise.reject(value.originData?.overExpiryValueLimitTips)
                  }
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <ExpiryConfigFormItem form={form} />
        </FormPro.Item>
      )
      }
    </>
  )
}

export default BudgetFormItems